import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  displayUnitLabel,
  useUnitDisplay,
  type QuantityKind,
} from "../../app/units/UnitDisplayContext";
import type { CatalogApi } from "../catalog/api";
import type { PumpTemplateListItem } from "../catalog/types";
import {
  curveTraceSupportsAffinity,
  type PublicationDraft,
} from "./publicationState";
import type { CurveDraftDocument } from "./types";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { curveImportFieldHelp } from "../../app/guidance/fieldHelpRegistry";

interface PublicationEditorProps {
  catalog: CatalogApi;
  showCatalogChoice?: boolean;
  groupLabel: string | null;
  document: CurveDraftDocument;
  index: number;
  onChange: (draft: PublicationDraft) => void;
  onRemove: () => void;
  otherTraceIds: Set<string>;
  publication: PublicationDraft;
  pumpTemplates: PumpTemplateListItem[];
  removable: boolean;
}

interface CatalogVariantOption {
  id: string;
  name: string;
}

function catalogVariants(value: unknown): CatalogVariantOption[] {
  if (!Array.isArray(value)) return [];
  const candidates: unknown[] = value;
  return candidates.flatMap((candidate) => {
    if (
      typeof candidate === "object" &&
      candidate !== null &&
      "id" in candidate &&
      "name" in candidate &&
      typeof candidate.id === "string" &&
      typeof candidate.name === "string"
    ) {
      return [{ id: candidate.id, name: candidate.name }];
    }
    return [];
  });
}
type ConvertedPublicationField =
  | "bepFlow"
  | "impellerDiameter"
  | "maximumFlow"
  | "maximumHead"
  | "maximumSuctionLift"
  | "mcfsFlow"
  | "minimumFlow"
  | "minimumHead"
  | "minimumSuctionPressure";

function conciseNumber(value: number): string {
  return Number(value.toPrecision(12)).toString();
}

export function PublicationEditor({
  catalog,
  showCatalogChoice = true,
  document,
  groupLabel,
  index,
  onChange,
  onRemove,
  otherTraceIds,
  publication,
  pumpTemplates,
  removable,
}: PublicationEditorProps) {
  const { toDisplayValue, toSiValue, units } = useUnitDisplay();
  const [affinitySourceId, setAffinitySourceId] = useState(
    document.traces.find(
      (trace) =>
        publication.measuredTraceIds.includes(trace.id) &&
        curveTraceSupportsAffinity(trace),
    )?.id ?? "",
  );
  const [affinityTargetSpeed, setAffinityTargetSpeed] = useState("");
  const selectedPump = useQuery({
    enabled:
      publication.linkCatalog &&
      publication.catalogTemplateId !== "" &&
      showCatalogChoice,
    queryFn: () => catalog.getPump(publication.catalogTemplateId),
    queryKey: [
      "flow-wizard",
      "curve-import-catalog-template",
      publication.catalogTemplateId,
    ],
  });
  const variantOptions = catalogVariants(
    selectedPump.data?.current_revision.variants,
  );
  const setupLabel = `Pump setup ${index + 1}`;

  const patch = (values: Partial<PublicationDraft>) =>
    onChange({ ...publication, ...values });
  const displayNumber = (value: string, quantity: QuantityKind): string => {
    if (value.trim() === "") return "";
    const parsed = Number(value);
    return Number.isFinite(parsed)
      ? conciseNumber(toDisplayValue(parsed, quantity))
      : value;
  };

  const patchDisplayNumber = (
    field: ConvertedPublicationField,
    value: string,
    quantity: QuantityKind,
  ) => {
    const parsed = Number(value);
    const canonical =
      value.trim() === "" || !Number.isFinite(parsed)
        ? value
        : conciseNumber(toSiValue(parsed, quantity));
    patch({ [field]: canonical } as Pick<
      PublicationDraft,
      ConvertedPublicationField
    >);
  };

  const toggleTrace = (traceId: string, checked: boolean) => {
    const measuredTraceIds = checked
      ? [...new Set([...publication.measuredTraceIds, traceId])]
      : publication.measuredTraceIds.filter((id) => id !== traceId);
    if (!measuredTraceIds.includes(affinitySourceId)) {
      setAffinitySourceId(
        document.traces.find(
          (trace) =>
            measuredTraceIds.includes(trace.id) &&
            curveTraceSupportsAffinity(trace),
        )?.id ?? "",
      );
    }
    patch({
      affinityRows: publication.affinityRows.filter((row) =>
        measuredTraceIds.includes(row.sourceTraceId),
      ),
      measuredTraceIds,
    });
  };

  const addAffinity = () => {
    const targetSpeedRpm = Number(affinityTargetSpeed);
    const affinitySource = document.traces.find(
      (trace) => trace.id === affinitySourceId,
    );
    if (
      affinitySourceId === "" ||
      !Number.isInteger(targetSpeedRpm) ||
      targetSpeedRpm <= 0 ||
      affinitySource === undefined ||
      !curveTraceSupportsAffinity(affinitySource)
    )
      return;
    if (
      publication.affinityRows.some(
        (row) =>
          row.sourceTraceId === affinitySourceId &&
          row.targetSpeedRpm === targetSpeedRpm,
      )
    ) {
      return;
    }
    patch({
      affinityRows: [
        ...publication.affinityRows,
        { sourceTraceId: affinitySourceId, targetSpeedRpm },
      ],
    });
  };

  const chooseTemplate = (templateId: string) => {
    const selected = pumpTemplates.find(
      (template) => template.id === templateId,
    );
    patch({
      catalogRevisionId: selected?.revision_id ?? "",
      catalogTemplateId: templateId,
      catalogVariantId: "",
    });
  };

  return (
    <fieldset className="curve-publication-card">
      <legend>{groupLabel ?? (removable ? setupLabel : "Pump setup")}</legend>
      <div className="curve-pump-choice">
        {!showCatalogChoice ? null : publication.linkCatalog ? (
          <>
            <label>
              Pump
              <select
                {...guided(
                  curveImportFieldHelp.catalogTemplate,
                  publication.id,
                )}
                required
                aria-label={"Publication " + (index + 1) + " pump template"}
                value={publication.catalogTemplateId}
                onChange={(event) => chooseTemplate(event.target.value)}
              >
                <option value="">Choose a pump</option>
                {pumpTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.template_name} — {template.manufacturer}{" "}
                    {template.model}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Impeller / variant
              <select
                {...guided(curveImportFieldHelp.catalogVariant, publication.id)}
                required
                aria-label={"Publication " + (index + 1) + " pump variant"}
                disabled={selectedPump.data === undefined}
                value={publication.catalogVariantId}
                onChange={(event) =>
                  patch({ catalogVariantId: event.target.value })
                }
              >
                <option value="">Choose a pump variant</option>
                {variantOptions.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name}
                  </option>
                ))}
              </select>
              {selectedPump.data === undefined && selectedPump.isLoading
                ? "Loading pump variants…"
                : null}
            </label>
          </>
        ) : (
          <p className="curve-verification-gate">
            This curve will be saved as a checked reference only. Open Advanced
            setup below to link it to a planning pump.
          </p>
        )}
        <label>
          Tested RPM
          <input
            {...guided(curveImportFieldHelp.publicationSpeed, publication.id)}
            required
            aria-label={"Publication " + (index + 1) + " tested speed"}
            min="1"
            step="1"
            type="number"
            value={publication.testedSpeed}
            onChange={(event) => patch({ testedSpeed: event.target.value })}
          />
        </label>
      </div>

      <details className="curve-publication-advanced">
        <summary aria-label={`${setupLabel} advanced setup`}>
          Advanced setup
        </summary>

        <label className="curve-inline-check">
          <input
            {...guided(curveImportFieldHelp.saveWithoutCatalog, publication.id)}
            aria-label={`${setupLabel} save without linking to a catalog pump`}
            checked={!publication.linkCatalog}
            type="checkbox"
            onChange={(event) =>
              patch({
                catalogRevisionId: "",
                catalogTemplateId: "",
                catalogVariantId: "",
                linkCatalog: !event.target.checked,
              })
            }
          />
          Save without linking to a catalog pump
        </label>

        <div className="curve-form-grid">
          <label>
            Internal curve name
            <input
              {...guided(curveImportFieldHelp.publicationName, publication.id)}
              required
              aria-label={"Publication " + (index + 1) + " curve-set name"}
              maxLength={200}
              readOnly={publication.curveSetId !== null}
              value={publication.name}
              onChange={(event) => patch({ name: event.target.value })}
            />
            {publication.curveSetId === null ? null : (
              <small>
                This saved curve name is permanent. Change the revision label
                instead.
              </small>
            )}
          </label>
          <label>
            Source revision
            <input
              {...guided(
                curveImportFieldHelp.publicationRevision,
                publication.id,
              )}
              required
              aria-label={"Publication " + (index + 1) + " revision label"}
              maxLength={100}
              value={publication.revisionLabel}
              onChange={(event) => patch({ revisionLabel: event.target.value })}
            />
          </label>
          <label>
            Impeller diameter ({displayUnitLabel(units.diameter)})
            <input
              {...guided(curveImportFieldHelp.impellerDiameter, publication.id)}
              aria-label={"Publication " + (index + 1) + " impeller diameter"}
              min="0"
              step="any"
              type="number"
              value={displayNumber(publication.impellerDiameter, "diameter")}
              onChange={(event) =>
                patchDisplayNumber(
                  "impellerDiameter",
                  event.target.value,
                  "diameter",
                )
              }
            />
          </label>
          {publication.linkCatalog ? (
            <label>
              Catalog revision
              <select
                {...guided(
                  curveImportFieldHelp.catalogRevision,
                  publication.id,
                )}
                aria-label={"Publication " + (index + 1) + " pump revision"}
                required
                value={publication.catalogRevisionId}
                onChange={(event) =>
                  patch({ catalogRevisionId: event.target.value })
                }
              >
                <option value="">Choose a pump revision</option>
                {pumpTemplates
                  .filter(
                    (template) => template.id === publication.catalogTemplateId,
                  )
                  .map((template) => (
                    <option
                      key={template.revision_id}
                      value={template.revision_id}
                    >
                      {template.revision_label} (revision{" "}
                      {template.revision_number})
                    </option>
                  ))}
              </select>
            </label>
          ) : null}
        </div>

        <fieldset>
          <legend>Included curve data</legend>
          <div className="curve-check-grid">
            {document.traces.map((trace) => {
              const assignedElsewhere = otherTraceIds.has(trace.id);
              return (
                <label key={trace.id}>
                  <input
                    {...guided(
                      curveImportFieldHelp.measuredTrace,
                      publication.id + ":" + trace.id,
                    )}
                    aria-label={`${setupLabel} include ${trace.label} ${trace.kind.replaceAll(
                      "_",
                      " ",
                    )}`}
                    checked={publication.measuredTraceIds.includes(trace.id)}
                    disabled={assignedElsewhere}
                    type="checkbox"
                    onChange={(event) =>
                      toggleTrace(trace.id, event.target.checked)
                    }
                  />
                  {trace.label} ({trace.kind.replaceAll("_", " ")})
                  {assignedElsewhere ? " — used by another setup" : ""}
                </label>
              );
            })}
            {publication.measuredTraceIds
              .filter(
                (traceId) =>
                  !document.traces.some((trace) => trace.id === traceId),
              )
              .map((traceId) => (
                <label key={traceId}>
                  <input
                    {...guided(
                      curveImportFieldHelp.measuredTrace,
                      publication.id + ":stale:" + traceId,
                    )}
                    aria-label={`${setupLabel} remove prior curve value ${traceId}`}
                    checked
                    type="checkbox"
                    onChange={() => toggleTrace(traceId, false)}
                  />
                  Prior curve value {traceId} — no longer in this draft
                </label>
              ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Affinity-law speed estimates</legend>
          <div className="curve-affinity-controls">
            <label>
              Source curve
              <select
                {...guided(curveImportFieldHelp.affinitySource, publication.id)}
                aria-label={"Publication " + (index + 1) + " affinity source"}
                value={affinitySourceId}
                onChange={(event) => setAffinitySourceId(event.target.value)}
              >
                {document.traces
                  .filter(
                    (trace) =>
                      publication.measuredTraceIds.includes(trace.id) &&
                      curveTraceSupportsAffinity(trace),
                  )
                  .map((trace) => (
                    <option key={trace.id} value={trace.id}>
                      {trace.label}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Target RPM
              <input
                {...guided(
                  curveImportFieldHelp.affinityTargetSpeed,
                  publication.id,
                )}
                aria-label={
                  "Publication " + (index + 1) + " affinity target speed"
                }
                min="1"
                step="1"
                type="number"
                value={affinityTargetSpeed}
                onChange={(event) => setAffinityTargetSpeed(event.target.value)}
              />
            </label>
            <button
              aria-label={`${setupLabel} add affinity estimate`}
              type="button"
              onClick={addAffinity}
            >
              Add estimate
            </button>
          </div>
          <ul>
            {publication.affinityRows.map((row) => (
              <li key={row.sourceTraceId + ":" + row.targetSpeedRpm}>
                {document.traces.find((trace) => trace.id === row.sourceTraceId)
                  ?.label ?? row.sourceTraceId}
                {" → "}
                {row.targetSpeedRpm} RPM{" "}
                <button
                  aria-label={`${setupLabel} remove ${row.targetSpeedRpm} RPM estimate from ${row.sourceTraceId}`}
                  type="button"
                  onClick={() =>
                    patch({
                      affinityRows: publication.affinityRows.filter(
                        (candidate) => candidate !== row,
                      ),
                    })
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </fieldset>

        <fieldset>
          <legend>Optional operating limits</legend>
          <div className="curve-form-grid">
            {(
              [
                ["minimumFlow", "Minimum flow", "flowRate"],
                ["mcfsFlow", "MCSF", "flowRate"],
                ["bepFlow", "BEP flow", "flowRate"],
                ["maximumFlow", "Maximum flow", "flowRate"],
                ["minimumHead", "Minimum head", "head"],
                ["maximumHead", "Maximum head", "head"],
                ["maximumSuctionLift", "Maximum suction lift", "head"],
                [
                  "minimumSuctionPressure",
                  "Minimum suction pressure, absolute",
                  "pressure",
                ],
              ] as const
            ).map(([field, label, quantity]) => (
              <label key={field}>
                {label} ({displayUnitLabel(units[quantity])})
                <input
                  {...guided(
                    curveImportFieldHelp.envelopeValue,
                    publication.id + ":" + field,
                  )}
                  aria-label={`${setupLabel} ${label}`}
                  min="0"
                  step="any"
                  type="number"
                  value={displayNumber(publication[field], quantity)}
                  onChange={(event) =>
                    patchDisplayNumber(field, event.target.value, quantity)
                  }
                />
              </label>
            ))}
          </div>
        </fieldset>

        {removable ? (
          <button
            aria-label={`${setupLabel} remove pump setup`}
            className="curve-danger"
            type="button"
            onClick={onRemove}
          >
            Remove this pump setup
          </button>
        ) : null}
      </details>
    </fieldset>
  );
}
